import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaStar, FaCheckCircle, FaTimesCircle, FaHotel, FaUmbrellaBeach } from "react-icons/fa";
import "../css/styles.css";

const AmboseliTsavoDiani = () => {
  return (
    <div className="safari-detail-page">
      {/* ===== HERO ===== */}
      <section className="safari-detail-hero">
        <div className="safari-detail-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776348744/10_Days_Amboseli_Tsavo_Taita_Hills_Safari_Diani_Beach_jlqkip.jpg"
            alt="Amboseli Tsavo Taita Hills Safari and Diani Beach"
          />
          <div className="safari-detail-hero-overlay" />
        </div>
        <div className="safari-detail-hero-content">
          <span className="safari-detail-eyebrow">Kenya Safari &amp; Beach</span>
          <h1>10 Days Amboseli – Tsavo – Taita Hills Safari + Diani Beach</h1>
          <p className="safari-detail-hero-sub">
            From Kilimanjaro's elephants to Tsavo's red landscapes and the turquoise shores of Diani Beach — adventure meets paradise.
          </p>
          <div className="safari-detail-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== OVERVIEW ===== */}
      <section className="safari-detail-overview">
        <div className="safari-detail-section-header">
          <span className="section-tag">Safari Overview</span>
          <h2>Wild Adventure &amp; Coastal Relaxation</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-text">
          <p>
            This safari is the perfect blend of wild adventure and coastal relaxation. Start your journey in the shadow of Mount Kilimanjaro in Amboseli National Park, where large elephant herds roam across open plains. Then explore the red-dusted landscapes and wildlife of Tsavo West and Taita Hills, with dramatic scenery, hidden waterholes, and unique lodges perched above the wilderness.
          </p>
          <p>
            After six days of thrilling game drives and African landscapes, unwind with four nights on the pristine white sands of Diani Beach, letting the turquoise waters and gentle Indian Ocean breeze wash over you. This journey combines Kenya's most iconic wildlife with the ultimate tropical escape.
          </p>
        </div>
      </section>

      {/* ===== HIGHLIGHTS ===== */}
      <section className="safari-detail-highlights">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Highlights</span>
          <h2>Safari Highlights</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-highlights-list">
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>See large elephant herds in Amboseli National Park with Kilimanjaro as a backdrop</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Explore the rugged beauty of Tsavo West National Park and Taita Hills Wildlife Sanctuary</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Optional visits to local Maasai and Taita communities for cultural immersion</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Relax and recharge with four nights in Diani, one of Kenya's most beautiful beaches</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Perfect combination of adventure, wildlife, and relaxation</p>
          </div>
        </div>
      </section>

      {/* ===== AT A GLANCE ===== */}
      <section className="safari-detail-glance">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Quick Info</span>
          <h2>Safari at a Glance</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-glance-grid">
          <div className="safari-detail-glance-card">
            <FaClock className="safari-detail-glance-icon" />
            <h4>Duration</h4>
            <p>10 Days / 9 Nights</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Start / End</h4>
            <p>Nairobi</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaUmbrellaBeach className="safari-detail-glance-icon" />
            <h4>Destinations</h4>
            <p>Amboseli &ndash; Tsavo West &ndash; Taita Hills &ndash; Diani Beach</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaCalendarAlt className="safari-detail-glance-icon" />
            <h4>Best Time to Travel</h4>
            <p>All year round</p>
          </div>
        </div>
      </section>

      {/* ===== ITINERARY ===== */}
      <section className="safari-detail-itinerary">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Day by Day</span>
          <h2>Day-by-Day Itinerary</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-days">
          {/* Day 1 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">1</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 1 — Arrival in Nairobi</h3>
              <p>
                Arrive in Nairobi and meet your Nyatwende representative. Transfer to your hotel to rest after your journey or explore local attractions like the Giraffe Centre or David Sheldrick Elephant Orphanage.
              </p>
              <p className="safari-detail-day-note">Overnight in Nairobi.</p>
            </div>
          </div>

          {/* Day 2 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">2</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 2 — Nairobi to Amboseli National Park</h3>
              <p>
                After breakfast, depart Nairobi and drive towards Amboseli National Park. Set against the backdrop of Mount Kilimanjaro, Amboseli is famous for its elephants, lions, and stunning landscapes.
              </p>
              <p>
                Arrive at your lodge in time for lunch, then enjoy your first game drive in the park as the golden afternoon light bathes the savannah.
              </p>
              <p className="safari-detail-day-note">
                Overnight in Amboseli. <br />
                <strong>Accommodation:</strong> Sentrim Amboseli (Budget) / Amboseli Serena (Midrange) / Ol Tukai Lodge (Luxury)
              </p>
            </div>
          </div>

          {/* Day 3 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">3</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 3 — Amboseli to Tsavo West National Park</h3>
              <p>
                After breakfast, leave Amboseli for Tsavo West National Park, known for its dramatic volcanic landscapes, lava flows, and hidden waterholes.
              </p>
              <p>
                Arrive in time for lunch at your lodge, then take an afternoon game drive. You may spot elephants, lions, giraffes, buffalo, and hippos in the park's lush areas. Optional stop at Mzima Springs, a series of crystal-clear pools home to hippos and crocodiles.
              </p>
              <p className="safari-detail-day-note">
                Overnight in Tsavo West. <br />
                <strong>Accommodation:</strong> Ngulia Safari Lodge (Budget) / Severin Safari Camp (Midrange) / Kilaguni Serena (Luxury)
              </p>
            </div>
          </div>

          {/* Day 4 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">4</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 4 — Tsavo West to Taita Hills Wildlife Sanctuary</h3>
              <p>
                After breakfast, journey to Taita Hills Wildlife Sanctuary, a hidden gem with dramatic landscapes, cliffs, and unique wildlife viewing.
              </p>
              <p>
                Enjoy an afternoon game drive and watch elephants, giraffes, lions, and other wildlife come to the waterholes. The lodge here is perfectly positioned to overlook the valley, making every sunset magical.
              </p>
              <p className="safari-detail-day-note">
                Overnight in Taita Hills. <br />
                <strong>Accommodation:</strong> Salt Lick Safari Lodge / Taita Hills Resort &amp; Spa
              </p>
            </div>
          </div>

          {/* Day 5 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">5</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 5 — Full Day in Taita Hills</h3>
              <p>
                Spend a full day exploring Taita Hills. Morning and afternoon game drives offer incredible views of the wildlife and the unique red hills. You may spot wildlife rarely seen in other parks, and the quiet, off-the-beaten-path feel makes it a special safari experience.
              </p>
              <p>
                Optional visits to local Taita villages allow you to experience cultural traditions and meet the communities that live alongside the wildlife.
              </p>
              <p className="safari-detail-day-note">Overnight in Taita Hills.</p>
            </div>
          </div>

          {/* Day 6 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">6</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 6 — Taita Hills to Diani Beach</h3>
              <p>
                After breakfast, depart Taita Hills and drive towards Mombasa, where you will board the standard gauge railway (SGR) to Diani Beach.
              </p>
              <p>
                Arrive in the afternoon at your oceanfront resort and take in the turquoise waters, swaying palms, and white sandy beaches. Relax, swim, or simply enjoy a cocktail as the Indian Ocean sun sets.
              </p>
              <p className="safari-detail-day-note">
                Overnight in Diani Beach. <br />
                <strong>Accommodation:</strong> Baobab Beach Resort (Midrange) / Almanara Luxury Villas (Luxury)
              </p>
            </div>
          </div>

          {/* Days 7–9 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">7‑9</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Days 7–9 — Diani Beach Relaxation</h3>
              <p>
                Spend three full days unwinding on the beach.
              </p>
              <p><strong>Optional activities include:</strong></p>
              <ul className="safari-detail-day-list">
                <li>Snorkeling or diving in Kisite-Mpunguti Marine Park</li>
                <li>Sunset dhow cruises along the coast</li>
                <li>Exploring local villages and markets</li>
                <li>Simply relaxing by the pool or on the sand</li>
              </ul>
              <p>
                These days are all about relaxation and reflection after your safari adventure.
              </p>
              <p className="safari-detail-day-note">Overnight in Diani Beach.</p>
            </div>
          </div>

          {/* Day 10 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">10</div>
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 10 — Diani Beach to Nairobi / Departure</h3>
              <p>
                After breakfast, transfer to Mombasa Airport for a domestic flight back to Nairobi. Connect with your international departure flight or stay overnight in Nairobi if required.
              </p>
              <p className="safari-detail-day-note">
                Your Kenyan adventure ends here, leaving you with memories of wild elephants, dramatic landscapes, and turquoise ocean waves.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ACCOMMODATION ===== */}
      <section className="safari-detail-accommodation">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Where You'll Stay</span>
          <h2>Accommodation Examples</h2>
          <div className="safari-detail-line" />
        </div>

        {/* Amboseli */}
        <h3 className="safari-detail-accom-region-title">Amboseli</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier budget">Budget</div>
            <ul>
              <li><FaHotel /> Sentrim Amboseli Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Midrange</div>
            <ul>
              <li><FaHotel /> Amboseli Serena Safari Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Ol Tukai Lodge</li>
            </ul>
          </div>
        </div>

        {/* Tsavo West */}
        <h3 className="safari-detail-accom-region-title">Tsavo West</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier budget">Budget</div>
            <ul>
              <li><FaHotel /> Ngulia Safari Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Midrange</div>
            <ul>
              <li><FaHotel /> Severin Safari Camp</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Kilaguni Serena Safari Lodge</li>
            </ul>
          </div>
        </div>

        {/* Taita Hills */}
        <h3 className="safari-detail-accom-region-title">Taita Hills</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Salt Lick Safari Lodge</li>
              <li><FaHotel /> Taita Hills Resort &amp; Spa</li>
            </ul>
          </div>
        </div>

        {/* Diani Beach */}
        <h3 className="safari-detail-accom-region-title">Diani Beach</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Midrange</div>
            <ul>
              <li><FaHotel /> Baobab Beach Resort</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Almanara Luxury Villas</li>
            </ul>
          </div>
        </div>

        <p className="safari-detail-accom-note">
          Similar lodges of the same standard may be used depending on availability.
        </p>
      </section>

      {/* ===== INCLUDED / EXCLUDED ===== */}
      <section className="safari-detail-inclusions">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">What to Expect</span>
          <h2>What's Included</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-inclusions-grid">
          <div className="safari-detail-inclusions-card included">
            <h3><FaCheckCircle /> Included</h3>
            <ul>
              <li>Airport transfers in Nairobi</li>
              <li>Transport in a comfortable safari vehicle with professional guide during safari</li>
              <li>Park entrance fees</li>
              <li>Accommodation during the safari and beach stay</li>
              <li>Meals as specified in the itinerary</li>
              <li>Bottled drinking water during game drives</li>
            </ul>
          </div>
          <div className="safari-detail-inclusions-card excluded">
            <h3><FaTimesCircle /> Not Included</h3>
            <ul>
              <li>International flights</li>
              <li>Travel insurance</li>
              <li>Visa fees</li>
              <li>Optional activities like dhow cruises, snorkeling, or village visits</li>
              <li>Personal expenses</li>
              <li>Tips and gratuities</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="safari-detail-cta">
        <div className="safari-detail-cta-content">
          <h2>Customize This Safari</h2>
          <p>
            This safari can be tailored to your needs: upgrade lodges, extend your beach stay, or combine with other destinations in Kenya or Tanzania.
          </p>
          <div className="safari-detail-cta-buttons">
            <Link to="/contact?safari=10 Days Amboseli Tsavo Taita Hills Safari Diani Beach" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/tours" className="hero-cta secondary">
              View More Safaris
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AmboseliTsavoDiani;
